import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * 149-cw-r178: SearchableSelect — dropdown yang bisa DIKETIK untuk filter
 * (user request: "semua kolom isian tetap bisa diketik untuk mencari manual,
 * tidak harus scroll"). Dipakai menggantikan native <select> / DropdownMenu
 * untuk daftar opsi panjang (project, cost center, minggu, dst).
 *
 * Props:
 *  - value: string
 *  - onChange: (value: string) => void
 *  - options: [{ value, label }]  ATAU
 *  - groups: [{ label, options: [{ value, label }] }]
 *  - placeholder, searchPlaceholder, emptyText
 *  - disabled, testid
 */
export default function SearchableSelect({
  value,
  onChange,
  options = null,
  groups = null,
  placeholder = "Pilih…",
  searchPlaceholder = "Ketik untuk mencari…",
  emptyText = "Tidak ada hasil yang cocok",
  disabled = false,
  testid,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const inputRef = useRef(null);

  // Normalisasi ke struktur groups agar render tunggal.
  const normGroups = useMemo(() => {
    if (groups) return groups;
    return [{ label: null, options: options || [] }];
  }, [groups, options]);

  const allOptions = useMemo(
    () => normGroups.flatMap((g) => g.options),
    [normGroups]
  );
  const selected = allOptions.find((o) => o.value === value) || null;

  const filteredGroups = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return normGroups;
    return normGroups
      .map((g) => ({
        ...g,
        options: g.options.filter((o) => (o.label || "").toLowerCase().includes(query)),
      }))
      .filter((g) => g.options.length > 0);
  }, [normGroups, q]);

  const totalFiltered = filteredGroups.reduce((n, g) => n + g.options.length, 0);

  useEffect(() => {
    if (open) {
      setQ("");
      // Fokus otomatis ke search box supaya user bisa langsung mengetik.
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  return (
    <Popover open={open} onOpenChange={disabled ? undefined : setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={testid}
          className="w-full h-9 border border-slate-300 bg-white px-3 text-left text-xs inline-flex items-center justify-between hover:border-[#0B1D3A] disabled:opacity-50 disabled:cursor-not-allowed rounded-none"
        >
          <span className={`truncate ${selected ? "text-slate-800" : "text-slate-400"}`}>
            {selected ? selected.label : placeholder}
          </span>
          <ChevronDown size={12} className="shrink-0 text-slate-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="p-0 rounded-none w-[--radix-popover-trigger-width] min-w-[18rem]"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="flex items-center gap-2 border-b border-slate-200 px-2">
          <Search size={13} className="text-slate-400 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={searchPlaceholder}
            data-testid={testid ? `${testid}-search` : undefined}
            className="flex-1 h-9 text-xs bg-transparent focus:outline-none placeholder:text-slate-400"
          />
        </div>
        <div
          className="max-h-[50vh] overflow-y-auto overscroll-contain"
          data-testid={testid ? `${testid}-list` : undefined}
          // 149-cw-r196: di dalam Dialog modal, react-remove-scroll menelan wheel
          // event portal Popover → list tak bisa di-scroll. stopPropagation fix.
          onWheel={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
        >
          {totalFiltered === 0 && (
            <div className="px-3 py-4 text-[11px] text-slate-400 italic text-center">{emptyText}</div>
          )}
          {filteredGroups.map((g, gi) => (
            <div key={g.label ?? `__group_${gi}`}>
              {g.label && (
                <div className="px-2 py-1.5 bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider font-bold text-slate-600 sticky top-0">
                  {g.label}
                </div>
              )}
              {g.options.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  data-testid={testid ? `${testid}-opt-${o.value}` : undefined}
                  onClick={() => { onChange(o.value); setOpen(false); }}
                  className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 border-b border-slate-100 last:border-0 transition-colors ${
                    o.value === value ? "bg-[#0B1D3A] text-white" : "bg-white text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  <Check size={12} className={`shrink-0 ${o.value === value ? "opacity-100" : "opacity-0"}`} />
                  <span className="truncate">{o.label}</span>
                </button>
              ))}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
